import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Play, Volume2 } from 'lucide-react';
import PageHeader from '@/components/PageHeader';

const VoiceTest = ({ t }) => {
  const [playing, setPlaying] = useState(null);
  const [currentAudio, setCurrentAudio] = useState(null);

  const voices = [
    {
      name: 'alloy',
      label: 'Alloy',
      description: 'Neutre-féminine, claire et légère',
      emoji: '👩',
      current: true
    },
    {
      name: 'nova',
      label: 'Nova',
      description: 'Féminine, chaleureuse et douce',
      emoji: '👩‍🦰'
    },
    {
      name: 'shimmer',
      label: 'Shimmer',
      description: 'Féminine, énergique et expressive',
      emoji: '👩‍🎤'
    },
    {
      name: 'fable',
      label: 'Fable',
      description: 'Britannique, storytelling, neutre',
      emoji: '🎭'
    },
    {
      name: 'onyx',
      label: 'Onyx (Auren)',
      description: 'Masculine, profonde, calme - PARFAITE',
      emoji: '👨',
      masculine: true
    }
  ];

  const testMessage = "Bonjour, je suis une voix apaisante pour Radio Hearen. Respirez profondément et détendez-vous.";

  const playVoice = async (voiceName) => {
    try {
      // Stop current audio if playing
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }

      setPlaying(voiceName);
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const url = `${backendUrl}/api/radio/test-voice/${voiceName}?text=${encodeURIComponent(testMessage)}`;
      
      const audio = new Audio(url);
      setCurrentAudio(audio);
      
      audio.onended = () => {
        setPlaying(null);
        setCurrentAudio(null);
      };
      
      audio.onerror = (e) => {
        console.error('Error playing audio:', e);
        setPlaying(null);
        setCurrentAudio(null);
        alert('Erreur lors de la lecture audio');
      };
      
      await audio.play();
      
    } catch (error) {
      console.error('Error:', error);
      setPlaying(null);
      setCurrentAudio(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8 pb-40">
      <div className="max-w-4xl mx-auto">
        <PageHeader t={t} />

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            🎤 Test des Voix Féminines
          </h1>
          <p className="text-gray-600">
            Écoutez les différentes voix disponibles pour Héra et choisissez votre préférée
          </p>
        </div>

        {/* Test Message */}
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-blue-900">Message de Test</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-blue-800 italic">"{testMessage}"</p>
          </CardContent>
        </Card>

        {/* Voice Cards */}
        <div className="space-y-4">
          {voices.map((voice) => (
            <Card 
              key={voice.name}
              className={`transition-all ${
                voice.current 
                  ? 'border-2 border-pink-500 bg-pink-50' 
                  : voice.masculine
                  ? 'border-2 border-blue-500 bg-blue-50'
                  : 'border-gray-200'
              }`}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-3xl">{voice.emoji}</span>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">
                          {voice.label}
                          {voice.current && (
                            <span className="ml-2 text-sm px-2 py-1 bg-pink-600 text-white rounded">
                              Actuelle pour Héra
                            </span>
                          )}
                          {voice.masculine && (
                            <span className="ml-2 text-sm px-2 py-1 bg-blue-600 text-white rounded">
                              Actuelle pour Auren
                            </span>
                          )}
                        </h3>
                        <p className="text-sm text-gray-600">{voice.description}</p>
                      </div>
                    </div>
                  </div>
                  
                  <Button
                    onClick={() => playVoice(voice.name)}
                    disabled={playing === voice.name}
                    className={`${
                      voice.masculine 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'bg-pink-600 hover:bg-pink-700'
                    } text-white px-6 py-6 rounded-full`}
                  >
                    {playing === voice.name ? (
                      <Volume2 className="h-6 w-6 animate-pulse" />
                    ) : (
                      <Play className="h-6 w-6 ml-1" />
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Instructions */}
        <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <p className="text-sm text-yellow-800">
            <strong>📝 Instructions:</strong>
            <br />
            1. Cliquez sur ▶️ pour écouter chaque voix
            <br />
            2. Notez quelle voix vous préférez
            <br />
            3. Dites-moi votre choix et je la configurerai pour Héra
            <br />
            <br />
            <strong>Recommandations pour voix apaisante:</strong>
            <br />
            • <strong>Alloy</strong>: La plus claire et légère (actuelle)
            <br />
            • <strong>Nova</strong>: Chaleureuse et douce
            <br />
            • <strong>Shimmer</strong>: Plus énergique, moins apaisante
          </p>
        </div>
      </div>
    </div>
  );
};

export default VoiceTest;
